// DocViewer — in-app document viewer.
//
// Per CLAUDE.md Iron Rule 11, every document opened from a result row /
// brief page / saved-screen / vendor profile MUST open in this primitive,
// not a new tab + native browser PDF chrome. Reasons:
//
//   * Audit fidelity: the in-app fetch goes through /v1/documents/:id/content
//     which emits a `document.viewed` audit row on every request. A native
//     browser tab would bypass our audit chain.
//   * UX: the operator stays inside the terminal — keyboard shortcuts,
//     density, dark surface — instead of being thrown into the browser's
//     own viewer chrome.
//   * Security: the API requires Bearer auth; iframes can't set custom
//     headers so bare iframe src= would 401. We fetch via JS (same pattern
//     as the TEXT pane) and pass a blob: URL to the iframe so the bytes
//     never touch a bearer-less request path.
//
// Two surfaces:
//   * Right-side <Drawer> with an embedded <iframe> sourced from a blob URL
//     built by a JS fetch with the Bearer token. Most opportunity attachments
//     are PDFs and Chrome/Edge/Safari/Firefox all render them inline.
//   * For non-PDF mime types the iframe still renders (HTML inline; DOCX/XLSX
//     trigger the browser's download flow). A "DOWNLOAD" button is the
//     fallback when the user wants to grab the binary.
//
// The Drawer is the existing 720px primitive. We deliberately key DocViewerBody
// on doc.id so switching documents fully unmounts the previous instance —
// this revokes the old blob URL and resets all pane state cleanly.

import { Drawer, Pill } from '@/primitives';
import { readAccessToken } from '@/state/auth';
import { useEffect, useRef, useState } from 'react';

export interface DocumentMeta {
  id: string;
  filename: string | null;
  mime_type: string;
  storage_size_bytes: number | null;
  extracted_pages: number | null;
  fetch_status: string;
  extraction_status: string;
  lang: string | null;
  amendment_no: string | null;
  /** Server-issued path to the byte stream (always /v1/documents/:id/content). */
  content_url: string;
  /** Set when extraction_status='extracted'. Powers the "TEXT" right-tab. */
  text_url: string | null;
  original_url: string | null;
}

export interface DocViewerProps {
  /** When non-null, the drawer is open and shows this document. */
  doc: DocumentMeta | null;
  /** Close affordance. */
  onClose: () => void;
  /** Path prefix for resolving relative content/text URLs (default '/api'
   *  to match the SPA's apiBaseUrl proxy). The auth-client wraps requests
   *  with the Bearer token via window.__EIDOLLON_ACCESS__. */
  apiBaseUrl?: string;
}

export function DocViewer({ doc, onClose, apiBaseUrl = '/api' }: DocViewerProps) {
  const contentSrc = doc ? `${apiBaseUrl}${doc.content_url}` : null;
  const textSrc = doc?.text_url ? `${apiBaseUrl}${doc.text_url}` : null;
  const externalUrl = doc?.original_url ?? null;
  const filename = doc?.filename ?? doc?.id.slice(0, 8) ?? '—';
  const isPdf = doc?.mime_type === 'application/pdf';

  // blobSrc: set by DocViewerBody once the fetch completes. Used by the
  // open-in-new-tab button. Null while loading or if fetch failed.
  const [blobSrc, setBlobSrc] = useState<string | null>(null);

  // Reset blobSrc when a different document opens (doc.id changes).
  useEffect(() => {
    setBlobSrc(null);
  }, [doc?.id]);

  return (
    <Drawer
      open={!!doc}
      onClose={onClose}
      title={`DOCUMENT · ${filename}`}
      wide
      actions={
        doc && contentSrc ? (
          <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {blobSrc ? (
              <a
                href={blobSrc}
                target="_blank"
                rel="noopener noreferrer"
                className="btn xs gh"
                title="Open in new tab"
              >
                ↗
              </a>
            ) : (
              <button type="button" className="btn xs gh" disabled title="loading…">
                ↗
              </button>
            )}
            <DownloadButton contentSrc={contentSrc} filename={doc.filename ?? `${doc.id}.pdf`} />
          </span>
        ) : null
      }
    >
      {doc && contentSrc ? (
        <DocViewerBody
          key={doc.id}
          doc={doc}
          contentSrc={contentSrc}
          textSrc={textSrc}
          externalUrl={externalUrl}
          isPdf={isPdf}
          onBlobReady={setBlobSrc}
        />
      ) : null}
    </Drawer>
  );
}

function DocViewerBody({
  doc,
  contentSrc,
  textSrc,
  externalUrl,
  isPdf,
  onBlobReady,
}: {
  doc: DocumentMeta;
  contentSrc: string;
  textSrc: string | null;
  externalUrl: string | null;
  isPdf: boolean;
  onBlobReady: (url: string) => void;
}) {
  const [pane, setPane] = useState<'viewer' | 'text' | 'info'>('viewer');

  // Viewer pane: fetch bytes with Bearer token → blob URL.
  // An iframe's src= load cannot set Authorization headers, so we use the
  // same JS fetch approach as the TEXT pane and hand a blob: URL to the
  // iframe. This also sidesteps the X-Frame-Options: DENY header the API's
  // securityHeaders middleware sets on all /v1/* responses.
  const [viewerSrc, setViewerSrc] = useState<string | null>(null);
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [viewerLoading, setViewerLoading] = useState(true);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const access = readAccessToken() ?? '';

    fetch(contentSrc, {
      headers: access ? { Authorization: `Bearer ${access}` } : {},
      credentials: 'include',
    })
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.text().catch(() => '');
          console.error('[DocViewer] content fetch failed', r.status, r.url, body);
          // 404 = no stored byte stream for this id (e.g. an intel signal that
          // only carries a source_url, or extraction hasn't run). Show a calm
          // empty state instead of a raw "document load 404" error string.
          if (!cancelled) {
            setViewerError(
              r.status === 404
                ? 'No viewable document for this item — it may only have a source link.'
                : `Couldn’t load the document (error ${r.status}).`,
            );
          }
          return null;
        }
        return r.blob();
      })
      .then((blob) => {
        if (cancelled || !blob) return;
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setViewerSrc(url);
        onBlobReady(url);
      })
      .catch((err) => {
        if (!cancelled) setViewerError(String(err));
      })
      .finally(() => {
        if (!cancelled) setViewerLoading(false);
      });

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [contentSrc, onBlobReady]);

  // Text pane: lazy-load extracted text when user switches to that tab.
  const [textLoaded, setTextLoaded] = useState<{ pages: number | null; text: string } | null>(null);
  const [textError, setTextError] = useState<string | null>(null);

  useEffect(() => {
    if (pane !== 'text' || !textSrc || textLoaded) return;
    const access = readAccessToken() ?? '';
    fetch(textSrc, {
      headers: access ? { Authorization: `Bearer ${access}` } : {},
      credentials: 'include',
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`text load ${r.status}`);
        return r.json() as Promise<{ pages: number | null; text: string }>;
      })
      .then((data) => setTextLoaded(data))
      .catch((err) => setTextError(String(err)));
  }, [pane, textSrc, textLoaded]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Tab strip */}
      <div
        style={{
          display: 'flex',
          height: 28,
          padding: '0 12px',
          borderBottom: '1px solid var(--bd)',
          background: 'var(--bg-pane)',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
        }}
      >
        <PaneTab active={pane === 'viewer'} onClick={() => setPane('viewer')}>
          VIEW
        </PaneTab>
        <PaneTab
          active={pane === 'text'}
          disabled={!textSrc}
          title={textSrc ? 'extracted text' : 'extraction pending'}
          onClick={() => textSrc && setPane('text')}
        >
          TEXT {doc.extracted_pages ? `(${doc.extracted_pages}p)` : ''}
        </PaneTab>
        <PaneTab active={pane === 'info'} onClick={() => setPane('info')}>
          INFO
        </PaneTab>
        <span style={{ flex: 1 }} />
        <span className="meta mono" style={{ color: 'var(--fg-3)' }}>
          {doc.mime_type}
          {doc.storage_size_bytes != null ? ` · ${formatBytes(doc.storage_size_bytes)}` : ''}
        </span>
      </div>

      {/* Pane body */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', background: 'var(--bg-base)' }}>
        {pane === 'viewer' &&
          (viewerLoading ? (
            <div
              className="meta mono"
              style={{ padding: 24, textAlign: 'center', color: 'var(--fg-3)' }}
            >
              loading document…
            </div>
          ) : viewerError ? (
            <div className="meta mono rd" style={{ padding: 24 }}>
              {viewerError}
            </div>
          ) : viewerSrc && isPdf ? (
            <iframe
              src={viewerSrc}
              title={doc.filename ?? 'document'}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                background: 'var(--bg-base)',
              }}
            />
          ) : viewerSrc ? (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <iframe
                src={viewerSrc}
                title={doc.filename ?? 'document'}
                style={{ flex: 1, border: 'none', background: 'var(--bg-base)' }}
                sandbox="allow-same-origin"
              />
              <div
                className="meta mono"
                style={{
                  padding: '8px 12px',
                  borderTop: '1px solid var(--bd)',
                  color: 'var(--fg-3)',
                  flexShrink: 0,
                }}
              >
                {doc.mime_type} may not render inline · use ↗ or ⤓ in the header.
              </div>
            </div>
          ) : null)}

        {pane === 'text' && (
          <div style={{ height: '100%', overflow: 'auto', padding: '12px 16px' }}>
            {textError ? (
              <div className="meta mono rd">text load failed: {textError}</div>
            ) : !textLoaded ? (
              <div className="meta mono">loading extracted text…</div>
            ) : (
              <pre
                style={{
                  margin: 0,
                  fontFamily: 'var(--mono)',
                  fontSize: 12,
                  color: 'var(--fg-1)',
                  lineHeight: 1.55,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {textLoaded.text}
              </pre>
            )}
          </div>
        )}

        {pane === 'info' && (
          <div style={{ padding: '12px 16px', overflow: 'auto' }}>
            <dl
              style={{
                margin: 0,
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                rowGap: 6,
                columnGap: 12,
              }}
            >
              <Term label="filename" value={doc.filename ?? '—'} />
              <Term label="mime type" value={doc.mime_type} />
              <Term
                label="size"
                value={doc.storage_size_bytes != null ? formatBytes(doc.storage_size_bytes) : '—'}
              />
              <Term label="lang" value={doc.lang ?? '—'} />
              <Term label="amendment" value={doc.amendment_no ?? '—'} />
              <Term
                label="fetch"
                value={
                  <Pill
                    tone={
                      doc.fetch_status === 'fetched'
                        ? 'green'
                        : doc.fetch_status === 'failed'
                          ? 'red'
                          : 'gray'
                    }
                  >
                    {doc.fetch_status}
                  </Pill>
                }
              />
              <Term
                label="extract"
                value={
                  <Pill
                    tone={
                      doc.extraction_status === 'extracted'
                        ? 'green'
                        : doc.extraction_status === 'failed'
                          ? 'red'
                          : 'gray'
                    }
                  >
                    {doc.extraction_status}
                  </Pill>
                }
              />
              <Term
                label="pages"
                value={doc.extracted_pages != null ? String(doc.extracted_pages) : '—'}
              />
              <Term label="sha256" value={<code style={{ fontSize: 10 }}>{doc.id}</code>} />
              {externalUrl && (
                <Term
                  label="source url"
                  value={
                    <a
                      href={externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--cyan, #2eb8c7)', wordBreak: 'break-all' }}
                    >
                      {externalUrl}
                    </a>
                  }
                />
              )}
            </dl>
          </div>
        )}
      </div>
    </div>
  );
}

// DownloadButton — fetches the document bytes with the Bearer token and
// triggers a browser download. This avoids the bare href= approach which
// would navigate without the Authorization header and get a 401.
function DownloadButton({ contentSrc, filename }: { contentSrc: string; filename: string }) {
  const [busy, setBusy] = useState(false);

  function handleClick() {
    if (busy) return;
    setBusy(true);
    const access = readAccessToken() ?? '';
    fetch(contentSrc, {
      headers: access ? { Authorization: `Bearer ${access}` } : {},
      credentials: 'include',
    })
      .then((r) => {
        if (!r.ok) throw new Error(`download ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch((err) => console.error('[DocViewer] download failed:', err))
      .finally(() => setBusy(false));
  }

  return (
    <button
      type="button"
      className="btn xs gh"
      title={busy ? 'downloading…' : 'Download'}
      disabled={busy}
      onClick={handleClick}
    >
      {busy ? '…' : '⤓'}
    </button>
  );
}

function PaneTab({
  active,
  disabled,
  title,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  title?: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        height: 28,
        padding: '0 8px',
        background: 'transparent',
        border: 'none',
        borderBottom: `2px solid ${active ? 'var(--primary)' : 'transparent'}`,
        color: disabled ? 'var(--fg-4)' : active ? 'var(--fg-1)' : 'var(--fg-3)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'var(--mono)',
        fontSize: 11,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
      }}
    >
      {children}
    </button>
  );
}

function Term({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt
        className="meta mono"
        style={{
          color: 'var(--fg-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          fontSize: 10,
        }}
      >
        {label}
      </dt>
      <dd style={{ margin: 0, fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--fg-1)' }}>
        {value}
      </dd>
    </>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}
